/**
 * PSV-1889 — daily-quest tracker HUD.
 *
 * Collapsed: one line under the buffs row — "QUESTS 1/3 ▸ [Q]".
 * Expanded (Q or click): today's three dailies with progress bars +
 * the achievements tally. Pure render — state arrives via the server's
 * QuestProgress pushes (see ArenaController.handleQuestProgress).
 */

import Phaser from 'phaser';

import {
  ACHIEVEMENTS,
  dailiesFor,
  type DailyDef,
  type QuestMetric,
} from '../../../server/shared/quests';
import type { Tooltip } from './Tooltip';

const PANEL_X = 10;
const DEFAULT_PANEL_Y = 88;
const PANEL_W = 240;
const ROW_H = 34;
const BAR_W = 190;
const BAR_H = 5;
const BG = 0x0b101c;
const BG_ALPHA = 0.82;
const COLOR_TITLE = '#7fd6ea';
const COLOR_TEXT = '#eeeeee';
const COLOR_DIM = '#9aa0b4';
const COLOR_DONE = '#44ff7a';
const BAR_BG = 0x223048;
const BAR_FILL = 0x35c4f0;
const BAR_DONE = 0x44ff7a;
const DEPTH = 18;

export interface QuestTrackerState {
  date: string;
  progress: Partial<Record<QuestMetric, number>>;
  claimed: string[];
  unlockedCount: number;
  /** PSV-1912 — unlocked achievement ids (tooltip check marks). */
  unlocked?: string[];
}

export class QuestTracker {
  private readonly scene: Phaser.Scene;
  private readonly tooltip: Tooltip | null;
  private container: Phaser.GameObjects.Container | null = null;
  private expanded = false;
  private state: QuestTrackerState | null = null;
  private destroyed = false;
  /** PSV-1902 UX — the top-left HUD column stacks dynamically (ability
   * line + N buff rows above us); the controller drives this offset. */
  private baseY = DEFAULT_PANEL_Y;
  private hovering = false;

  constructor(scene: Phaser.Scene, tooltip: Tooltip | null = null) {
    this.scene = scene;
    this.tooltip = tooltip;
    this.render();
  }

  /**
   * PSV-1912 — hover info: daily rows explain themselves, the
   * achievements line lists every achievement with its unlock state.
   * Driven by the controller's pointermove (screen coords).
   */
  handleHover(p: { x: number; y: number } | null): void {
    if (this.tooltip === null) return;
    if (p === null || !this.expanded || this.state === null || !this.containsPointer(p)) {
      if (this.hovering) {
        this.hovering = false;
        this.tooltip.hide();
      }
      return;
    }
    const localY = p.y - this.baseY;
    const dailies = this.rows();
    const rowIndex = Math.floor((localY - 26) / ROW_H);
    let text: string | null = null;
    if (rowIndex >= 0 && rowIndex < dailies.length) {
      const d = dailies[rowIndex]!;
      text = `${d.name}\nDaily quest — resets at UTC midnight.\nReward: +${d.rewardGems} ◆ straight to your hold.`;
    } else if (localY >= 26 + dailies.length * ROW_H) {
      const lines = ACHIEVEMENTS.map(
        (a) =>
          `${this.state!.unlocked?.includes?.(a.id) ?? false ? '✓' : '✗'} ${a.name} — ${a.desc}`,
      );
      text = `Achievements (one-shot, permanent):\n${lines.join('\n')}`;
    }
    if (text === null) {
      if (this.hovering) {
        this.hovering = false;
        this.tooltip.hide();
      }
      return;
    }
    this.hovering = true;
    this.tooltip.show(text, p.x + 14, p.y + 8);
  }

  setState(state: QuestTrackerState): void {
    this.state = state;
    this.render();
  }

  toggle(): void {
    this.expanded = !this.expanded;
    this.render();
  }

  setBaseY(y: number): void {
    if (this.baseY === y) return;
    this.baseY = y;
    this.container?.setY(y);
  }

  /** Click-through guard for the fire handler (same as panels). */
  containsPointer(p: { x: number; y: number }): boolean {
    const h = this.expanded ? 30 + this.rows().length * ROW_H + 22 : 24;
    return (
      p.x >= PANEL_X && p.x <= PANEL_X + PANEL_W && p.y >= this.baseY && p.y <= this.baseY + h
    );
  }

  destroy(): void {
    this.destroyed = true;
    this.container?.destroy(true);
    this.container = null;
  }

  // ---------- private ----------

  private rows(): DailyDef[] {
    return this.state !== null ? dailiesFor(this.state.date) : [];
  }

  private render(): void {
    if (this.destroyed) return;
    this.container?.destroy(true);
    this.container = this.scene.add
      .container(PANEL_X, this.baseY)
      .setScrollFactor(0)
      .setDepth(DEPTH);

    const dailies = this.rows();
    const doneCount =
      this.state === null
        ? 0
        : dailies.filter((d) => this.state!.claimed.includes(d.id)).length;

    const header = this.scene.add.text(
      0,
      0,
      `QUESTS ${doneCount}/${dailies.length || 3} ${this.expanded ? '▾' : '▸'} [Q]`,
      { fontSize: '12px', color: COLOR_TITLE, fontStyle: 'bold' },
    );
    this.container.add(header);
    if (!this.expanded || this.state === null) return;

    const bg = this.scene.add.graphics();
    bg.fillStyle(BG, BG_ALPHA);
    bg.fillRoundedRect(-6, 18, PANEL_W, dailies.length * ROW_H + 26, 6);
    this.container.addAt(bg, 0);

    dailies.forEach((d, i) => {
      const y = 26 + i * ROW_H;
      const done = this.state!.claimed.includes(d.id);
      const raw = this.state!.progress[d.metric] ?? 0;
      const cur = Math.min(d.target, raw);
      const label = this.scene.add.text(
        0,
        y,
        `${done ? '✓ ' : ''}${d.name}  ·  +${d.rewardGems} ◆`,
        { fontSize: '11px', color: done ? COLOR_DONE : COLOR_TEXT },
      );
      const bar = this.scene.add.graphics();
      bar.fillStyle(BAR_BG, 1);
      bar.fillRect(0, y + 16, BAR_W, BAR_H);
      bar.fillStyle(done ? BAR_DONE : BAR_FILL, 1);
      bar.fillRect(0, y + 16, BAR_W * (done ? 1 : cur / d.target), BAR_H);
      const count = this.scene.add.text(
        BAR_W + 6,
        y + 12,
        done ? 'done' : `${cur}/${d.target}`,
        { fontSize: '10px', color: done ? COLOR_DONE : COLOR_DIM },
      );
      this.container!.add([label, bar, count]);
    });

    const achLine = this.scene.add.text(
      0,
      26 + dailies.length * ROW_H + 2,
      `Achievements: ${this.state.unlockedCount}/${ACHIEVEMENTS.length}`,
      { fontSize: '10px', color: COLOR_DIM },
    );
    this.container.add(achLine);
  }
}
